import React from 'react';
import Footer from '../components/Footer';
import SEOHead from '../seo/SEOHead';
import { PAGE_SEO } from '../seo/seoConfig';


const PrivacyPolicy = () => {
  return (
    <>
      <SEOHead
        title={PAGE_SEO.privacy.title}
        description={PAGE_SEO.privacy.description}
        keywords={PAGE_SEO.privacy.keywords}
        canonical={PAGE_SEO.privacy.canonical}
        ogImage={PAGE_SEO.privacy.ogImage}
      />
      <div className="policy-container">
        <div className="policy-content">
          <h1 className="policy-title">Privacy Policy</h1>

          <section className="policy-section">
            <h2 className="section-title">1. Introduction</h2>
            <p className="policy-text">
              RealFlame (operated by <strong>PEARL PRINT PACK PRIVATE LIMITED</strong>) values your privacy and is committed to protecting your personal data. This Privacy Policy describes how we collect, use, disclose, and safeguard your personal information when you visit our website, submit inquiries, or interact with our services.
            </p>
            <p className="policy-text">
              This policy applies to all website visitors and customers, and is designed to comply with applicable Indian data protection regulations as well as Meta's requirements for WhatsApp Business integration.
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">2. Information We Collect</h2>
            <p className="policy-text">
              We collect information to provide better services to our users and process your product inquiries effectively:
            </p>
            <h3 className="subsection-title">Information You Provide Directly</h3>
            <ul className="policy-list">
              <li className="policy-list-item"><strong>Full Name:</strong> Required to identify you for customer service and orders.</li>
              <li className="policy-list-item"><strong>Mobile Number:</strong> Required for communication via phone, SMS, and WhatsApp.</li>
              <li className="policy-list-item"><strong>Email Address:</strong> Required for sending confirmations, updates, and service information.</li>
              <li className="policy-list-item"><strong>Full Delivery Address:</strong> Required for delivery, logistics, and regional support.</li>
              <li className="policy-list-item"><strong>State, City & PIN Code:</strong> Required to verify delivery coverage and logistics.</li>
              <li className="policy-list-item"><strong>Landmark (Optional):</strong> Helps logistics partners locate your address accurately.</li>
            </ul>

            <h3 className="subsection-title">Information Collected Automatically</h3>
            <ul className="policy-list">
              <li className="policy-list-item"><strong>Device & Browser Data:</strong> IP address, browser type, operating system, and device identifier.</li>
              <li className="policy-list-item"><strong>Usage Information:</strong> Pages viewed, time spent on pages, search queries, and navigation paths.</li>
              <li className="policy-list-item"><strong>Cookies & Analytics:</strong> Cookies and web beacons to improve user experience and monitor performance.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2 className="section-title">3. How We Use Customer Information</h2>
            <p className="policy-text">We use the collected information for the following specific purposes:</p>
            <ul className="policy-list">
              <li className="policy-list-item">To process your product inquiries and facilitate your purchases via trusted partners (e.g. Flipkart).</li>
              <li className="policy-list-item">To communicate with you regarding your inquiries, orders, and customer service requests via Call, SMS, Email, and WhatsApp.</li>
              <li className="policy-list-item">To send order confirmations, delivery updates, and transactional notifications.</li>
              <li className="policy-list-item">To provide updates on new products, special offers, and promotions (only with your explicit consent).</li>
              <li className="policy-list-item">To analyze website performance and enhance customer experience.</li>
              <li className="policy-list-item">To prevent fraudulent transactions and maintain website security.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2 className="section-title">4. Data Sharing with Third Parties</h2>
            <p className="policy-text">
              We respect your privacy and <strong>never sell your personal data</strong> to third parties. We only share necessary data with trusted service providers who help us operate our business:
            </p>
            <ul className="policy-list">
              <li className="policy-list-item"><strong>Meta / WhatsApp Business API:</strong> We share your mobile number and communication logs with Meta to enable messaging via WhatsApp Business API.</li>
              <li className="policy-list-item"><strong>Logistics & Delivery Partners:</strong> Delivery address and contact numbers are shared with logistics services to deliver products.</li>
              <li className="policy-list-item"><strong>Analytics Providers:</strong> Services such as Google Analytics assist us in optimizing our website functionality.</li>
              <li className="policy-list-item"><strong>Legal Obligations:</strong> We may disclose data if required by Indian law, court order, or governmental regulations.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2 className="section-title">5. User Consent</h2>
            <p className="policy-text">
              By submitting any form on our website (including the Customer Details Form) and checking the consent box, you explicitly grant consent to RealFlame to contact you via:
            </p>
            <ul className="policy-list">
              <li className="policy-list-item"><strong>WhatsApp Messages:</strong> Using Meta's WhatsApp Business API for transactional updates and support.</li>
              <li className="policy-list-item"><strong>SMS & Voice Calls:</strong> For urgent service updates and inquiry verification.</li>
              <li className="policy-list-item"><strong>Email Communications:</strong> For detailed product specs, order details, and support replies.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2 className="section-title">6. Opt-Out Policy</h2>
            <p className="policy-text">
              You have full control over promotional communications. You may opt out at any time by:
            </p>
            <ul className="policy-list">
              <li className="policy-list-item">Replying <strong>"STOP"</strong> to any WhatsApp or SMS communication received from us.</li>
              <li className="policy-list-item">Sending an email with the subject "UNSUBSCRIBE" to <a href="mailto:info@realflame.in" className="policy-link">info@realflame.in</a>.</li>
              <li className="policy-list-item">Calling customer support at <a href="tel:+917310777481" className="policy-link">+91 73107 77481</a>.</li>
            </ul>
            <p className="policy-text">
              <em>Note: Essential transactional communications (such as order confirmations or ongoing inquiry resolution) cannot be opted out of as they are required for service fulfillment.</em>
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">7. Cookies Policy</h2>
            <p className="policy-text">
              Our website uses cookies to distinguish you from other users and provide a custom experience. We use:
            </p>
            <ul className="policy-list">
              <li className="policy-list-item"><strong>Essential Cookies:</strong> Required for site navigation and security.</li>
              <li className="policy-list-item"><strong>Analytics Cookies:</strong> Help us measure site traffic and user interactions.</li>
            </ul>
            <p className="policy-text">
              You can modify your browser settings to reject or delete cookies, though some site functions may become restricted.
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">8. Data Security</h2>
            <p className="policy-text">
              We employ strict administrative, technical, and physical safeguards to protect your personal data against unauthorized access, destruction, loss, or alteration. These include SSL/TLS encryption for data transmission, restricted data access controls, and secure server environments.
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">9. Children's Privacy</h2>
            <p className="policy-text">
              Our website and appliances are intended for adult consumers. We do not knowingly collect personal information from children under 18 years of age. If you believe a child has provided us with personal information, please contact us immediately for deletion.
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">10. Third-Party Links</h2>
            <p className="policy-text">
              Our website contains links to third-party websites (such as Flipkart). We are not responsible for the privacy practices, policies, or content of those third-party sites. We encourage you to read their privacy statements when leaving our website.
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">11. Legal Compliance</h2>
            <p className="policy-text">
              This Privacy Policy is governed by and complies with applicable Indian laws, including the Information Technology Act, 2000, and rules framed thereunder, as well as Meta Platform Terms and Developer Policies for WhatsApp Business API.
            </p>
          </section>

          <section className="policy-section">
            <h2 className="section-title">12. Contact Information for Privacy Inquiries</h2>
            <p className="policy-text">
              If you have any questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact our legal entity:
            </p>
            <div className="policy-contact-box">
              <p><strong>PEARL PRINT PACK PRIVATE LIMITED</strong></p>
              <p><strong>Registered Office:</strong> Plot No. 12, Sector – IIDC, Haridwar, Uttarakhand 249403, India</p>
              <p><strong>Email:</strong> <a href="mailto:info@realflame.in" className="policy-link">info@realflame.in</a></p>
              <p><strong>Phone:</strong> <a href="tel:+917310777481" className="policy-link">+91 73107 77481</a></p>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

const styles = `
  .policy-container {
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 40px var(--left-right-padding);
    font-family: var(--font-family-tertiary);
    background-color: var(--color-7, #FFFFFF);
  }

  .policy-content {
    width: 100%;
    max-width: 1000px;
    padding: var(--spacing-4) 0;
    color: #1F1F1F;
  }

  .policy-title {
    font-family: var(--font-family-primary);
    font-size: 32px;
    font-weight: 700;
    color: #F58220;
    margin-bottom: 8px;
  }

  .policy-last-updated {
    font-size: 14px;
    color: #666666;
    margin-bottom: 32px;
  }

  .policy-section {
    margin-bottom: 32px;
    padding: 0;
  }

  .section-title {
    font-family: var(--font-family-primary);
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #F58220;
    border-bottom: 2px solid #F8F8F8;
    padding-bottom: 6px;
  }

  .subsection-title {
    font-size: 17px;
    font-weight: 600;
    color: #1F1F1F;
    margin: 16px 0 8px 0;
  }

  .policy-text {
    font-size: 15px;
    line-height: 1.8;
    margin-bottom: 12px;
    color: #444444;
  }

  .policy-list {
    list-style-type: disc;
    padding-left: 24px;
    margin-bottom: 16px;
  }

  .policy-list-item {
    font-size: 15px;
    line-height: 1.7;
    margin-bottom: 8px;
    color: #444444;
  }

  .policy-list-item strong {
    color: #1F1F1F;
  }

  .policy-link {
    color: #F58220;
    text-decoration: none;
    font-weight: 500;
  }

  .policy-link:hover {
    text-decoration: underline;
    color: #E97700;
  }

  .policy-contact-box {
    background: #F8F8F8;
    border-left: 4px solid #F58220;
    padding: 20px;
    border-radius: 8px;
    margin-top: 16px;
  }

  .policy-contact-box p {
    margin-bottom: 8px;
    font-size: 15px;
  }

  @media (max-width: 768px) {
    .policy-title {
      font-size: 26px;
    }
    
    .section-title {
      font-size: 19px;
    }
    
    .policy-text, .policy-list-item {
      font-size: 14px;
    }
  }
`;

const PrivacyPolicyWithStyles = () => {
  return (
    <>
      <SEOHead
        title={PAGE_SEO.privacy.title}
        description={PAGE_SEO.privacy.description}
        keywords={PAGE_SEO.privacy.keywords}
        canonical={PAGE_SEO.privacy.canonical}
        ogImage={PAGE_SEO.privacy.ogImage}
      />
      <style>{styles}</style>
      <PrivacyPolicy />
    </>
  );
};

export default PrivacyPolicyWithStyles;